import { DrmScheme, MediaType, PlaybackOptions } from '@quickplay/player-interface';
import { TritonPlayerBuilder } from '../TiritonPlayerBuilder';
import { ExternalPlayer, ThumbnailPreviewConfig } from '@quickplay/player';
import { Logger, LoggerLevel, createLogger } from '@quickplay/foundation';
import { DefaultTritonPlayer } from './DefaultTritonPlayer';
import { TritonAudioStreamConnectionConfig } from '../TritonAudioStreamConnection';

const TAG_NAME = 'FL_PLAYER';

export function createTritonPlayerBuilder(): TritonPlayerBuilder {
  let contentUrl!: string;
  let mediaFormatType: MediaType = MediaType.HLS;
  let licenseUrl = '';
  let drmType: DrmScheme = DrmScheme.NONE;
  const options: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    enableEngagementScore: false,
  };
  let videoElement: HTMLVideoElement;
  let videoWrapperElement: HTMLElement;
  let extPlayer: ExternalPlayer;
  let serverCertificate: ArrayBuffer;
  let sideloadedtracks: any[];
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  let config: ThumbnailPreviewConfig;
  let thumbnailPreviewEndpointUrl: string | undefined;
  let library: any;
  let companionBannerContainers: Element[];

  const audioSbmConnectionConfig: TritonAudioStreamConnectionConfig = {
    sidebandMetadataUrl: '',
  };
  let instance: any;

  return {
    get contentUrl(): string {
      return contentUrl;
    },
    get extPlayer(): any {
      return extPlayer || ExternalPlayer.ShakaPlayer;
    },

    get mediaFormatType(): MediaType {
      return mediaFormatType || MediaType.DASH;
    },

    get licenseUrl(): string {
      return licenseUrl || '';
    },

    get drmType(): DrmScheme {
      return drmType || DrmScheme.WIDEVINE;
    },

    get options(): PlaybackOptions {
      return options;
    },

    get configuredLogger(): Logger {
      return defaultLogger;
    },

    get videoElement(): HTMLVideoElement {
      return videoElement;
    },

    get videoWrapper(): HTMLElement {
      return videoWrapperElement;
    },

    get serverCertificate(): ArrayBuffer {
      return serverCertificate;
    },

    get getSideLoadedTracks(): any[] {
      return sideloadedtracks;
    },

    get config(): ThumbnailPreviewConfig {
      return config;
    },

    get thumbnailPreviewEndpointUrl(): string | undefined {
      return thumbnailPreviewEndpointUrl;
    },

    get library(): any {
      return library;
    },

    get audioStreamConnectionConfig(): TritonAudioStreamConnectionConfig {
      return audioSbmConnectionConfig;
    },

    get libInstance(): any {
      return instance;
    },

    get companionBannerContainers(): Element[] {
      return companionBannerContainers;
    },

    mediaElement(element: HTMLVideoElement): TritonPlayerBuilder {
      videoElement = element;
      return this;
    },

    mediaWrapperElement(element: HTMLElement): TritonPlayerBuilder {
      videoWrapperElement = element;
      return this;
    },

    enableThumbnailPreview(
      thumbnailPreviewEndpointUrl_: string,
      config_: ThumbnailPreviewConfig = {},
    ): TritonPlayerBuilder {
      thumbnailPreviewEndpointUrl = thumbnailPreviewEndpointUrl_;
      config = config_;
      return this;
    },

    mediaUrl(mediaUrl: string): TritonPlayerBuilder {
      contentUrl = mediaUrl;
      return this;
    },

    mediaType(type: MediaType): TritonPlayerBuilder {
      mediaFormatType = type;
      return this;
    },

    drmLicenseUrl(drmLicenseUrl: string): TritonPlayerBuilder {
      licenseUrl = drmLicenseUrl;
      return this;
    },

    drmScheme(drmScheme: DrmScheme): TritonPlayerBuilder {
      drmType = drmScheme;
      return this;
    },

    playbackOptions(playbackOptions: PlaybackOptions): TritonPlayerBuilder {
      Object.assign(options, playbackOptions);
      return this;
    },

    usePlayer(externalPlayer: ExternalPlayer, libInstance?: any): TritonPlayerBuilder {
      extPlayer = externalPlayer;
      instance = libInstance;
      return this;
    },

    certificate(certificate: ArrayBuffer): TritonPlayerBuilder {
      serverCertificate = certificate;
      return this;
    },

    sideCarTracks(vttTracks: any[]): TritonPlayerBuilder {
      sideloadedtracks = vttTracks;
      return this;
    },

    logger(logger: Logger): TritonPlayerBuilder {
      defaultLogger = logger;
      return this;
    },

    playerLibrary(playerLib: any): TritonPlayerBuilder {
      library = playerLib;
      return this;
    },

    tritonAudioStreamConnectionConfig(
      audioStreamConnectionConfig: TritonAudioStreamConnectionConfig,
    ): TritonPlayerBuilder {
      Object.assign(audioSbmConnectionConfig, audioStreamConnectionConfig);
      return this;
    },

    syncBannerContainers(bannerContainers: Element[]): TritonPlayerBuilder {
      companionBannerContainers = bannerContainers;
      return this;
    },

    build(): DefaultTritonPlayer {
      return new DefaultTritonPlayer(this);
    },
  };
}
